<template>
  <el-dialog
    :visible="visible"
    :title="$t('shareDialog.title')"
    width="1000px"
    custom-class="shareDialog"
    :close-on-click-modal="false"
    :destroy-on-close="true"
    @close="handleClose"
  >
    <header
      class="shareDialog--header"
      slot="title"
    >
      <span>{{ $t('shareDialog.title') }}</span>
    </header>
    <div class="shareDialog--body">
      <el-row :gutter="20">
        <el-col :span="20">
          <el-input
            size="mini"
            :value="shareUrl"
            :disabled="!shareUrl"
            :placeholder="$t('shareDialog.placeholder')"
            readonly
          />
        </el-col>
        <el-col :span="4">
          <el-button
            size="mini"
            type="primary"
            :disabled="!shareUrl"
            @click="handleCopy"
          >
            {{ $t('shareDialog.copy') }}
          </el-button>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="20">
          <div class="shareDialog--body-time">
            <div class="shareDialog--body-time-item">
              <i
                class="el-icon-date"
                style="font-size: 16px; margin-right: 8px"
              />
              <span style="margin-right: 8px">{{ $t('shareDialog.expireTime') }}</span>
              <el-select
                size="mini"
                v-model="shareTime"
              >
                <el-option
                  v-for="item in shareTimeOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </div>
            <el-button
              size="mini"
              :loading="shareLoading"
              @click="handleUpdate"
            >{{ shareUrl ? $t('shareDialog.update') : $t('shareDialog.create') }}</el-button>
          </div>
        </el-col>
      </el-row>
      <div class="table-container">
        <div class="table-filter">
          <img
            src="@assets/codeScan/notification-icon-filter.png"
            alt="Filter Icon"
            class="filter-icon"
          />
          <el-form
            :model="filter"
            :inline="true"
            :show-message="false"
          >
            <el-form-item :label="$t('ReportsView.Severity')">
              <el-select
                size="mini"
                clearable
                multiple
                v-model="filter.status"
                :placeholder="$t('ReportsView.Select')"
              >
                <el-option
                  v-for="item in severityOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item :label="$t('ReportsView.Vulnerability')">
              <el-input
                size="mini"
                :placeholder="$t('ReportsView.Search')"
                v-model="filter.vulnerability"
              />
            </el-form-item>
          </el-form>
          <el-button
            size="mini"
            type="primary"
            @click="handleSearch"
          >
            {{ $t('ReportsView.SEARCH') }}
          </el-button>
        </div>
        <el-table
          :data="tableData"
          size="mini"
          style="width: 100%"
        >
          <el-table-column
            show-overflow-tooltip
            prop="vulId"
            :label="$t('ReportsView.ID')"
            :formatter="(row, col, vulId) => vulId.slice(0, 8)"
          />
          <el-table-column
            prop="severity"
            :label="$t('ReportsView.Severity')"
          >
            <template slot-scope="scope">
              <severity-tag :severity="scope.row.severity" />
            </template>
          </el-table-column>
          <el-table-column
            prop="category"
            :label="$t('ReportsView.Vulnerability')"
          >
            <template slot-scope="scope">
              <el-tooltip
                placement="top"
                width="300"
                trigger="hover"
              >
                <el-tag
                  style="cursor: default;max-width: 100%;text-overflow: ellipsis;overflow: hidden;"
                  :type="getVulnerabilityTagType(scope.row.category)"
                  size="small"
                >
                  {{ scope.row.category }}
                </el-tag>
                <div
                  class="tipContent"
                  slot="content"
                >
                  <strong>{{scope.row.category}}</strong>
                  <div style="border-bottom: 2px dashed #ffffff; padding: 5px"></div>
                  <div style="padding: 5px">
                    {{ scope.row.description }}
                  </div>
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column
            prop="price"
            :label="$t('ReportsView.Payment')"
          >
            <template slot-scope="scope">
              <el-tag
                size="small"
                :type="scope.row.purchaseStatus === 'PENDING' ? 'warning' : 'success'"
                v-if="!scope.row.price"
              >{{scope.row.purchaseStatus === 'PENDING' ? $t('ReportsView.PENDING') : $t('ReportsView.PAID')}}</el-tag>
              <el-tag
                v-else
                size="small"
              >{{ $t('ReportsView.WAITING') }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column
            prop="Details"
            :label="$t('ReportsView.Details')"
            width="100"
          >
            <template slot-scope="scope">
              <el-button
                :disabled="!shareUrl"
                :class="scope.row.purchaseStatus == 'PURCHASED' ? 'pay-view' : 'pay-buy'"
                @click="handleViewDetails(scope.row)"
              >
                {{ $t('ReportsView.VIEW') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-if="vulList.length > pageSize"
          style="margin-top: 16px; text-align: right"
          background
          size="mini"
          layout="prev, pager, next"
          :current-page="page"
          :page-size="pageSize"
          :total="vulList.length"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import SeverityTag from '@/components/severity-tag';
import { SeverityOptions } from '@/enum';
import { mapGetters } from 'vuex';
import { updateVulShareTime, getVulShareId } from '@/api/base';
import { copyText } from '@/utils/copy-text';

export const createShareUrl = (taskId, shareId) => `${window.location.origin}/report/task/${taskId}/${shareId}`;

const shareTimeOptions = [
  {
    label: '7 days',
    value: 7,
  },
  {
    label: '30 days',
    value: 30,
  },
  {
    label: '60 days',
    value: 60,
  },
];

export default {
  name: 'ShareDialog',
  components: {
    SeverityTag,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shareId: {
      type: String,
      default: '',
    },
    taskId: {
      type: String,
      default: '',
    },
    vulList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      severityOptions: SeverityOptions,
      shareTimeOptions,
      shareTime: shareTimeOptions[0].value,
      localShareId: this.shareId,
      filter: {
        status: [],
        vulnerability: '',
      },
      page: 1,
      pageSize: 5,
      currFilter: {
        status: [],
        vulnerability: '',
      },
      shareLoading: false,
    };
  },
  computed: {
    ...mapGetters('user', ['user', 'publicEmail']),
    shareUrl() {
      if (!this.localShareId) {
        return '';
      }

      return createShareUrl(this.taskId, this.localShareId);
    },
    shareText() {
      const userName = this.user.fullName || this.publicEmail;

      return `
        ${this.$t('shareDialog.accessInstructions', { userName })}

        ${this.$t('shareDialog.sharingLink')}: ${this.shareUrl}

        ${this.$t('shareDialog.expiration', { days: this.shareTime })}

        ${this.$t('shareDialog.securityWarning')}
      `;
    },
    tableData() {
      const orderSeverity = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

      return this.vulList
        .slice((this.page - 1) * this.pageSize, this.page * this.pageSize)
        .filter((item) => {
          const severityMatch = this.currFilter.status.length === 0 || this.currFilter.status.includes(item.severity);
          const vulnerabilityMatch = this.currFilter.vulnerability.length === 0 || item.category.includes(this.currFilter.vulnerability);

          return severityMatch && vulnerabilityMatch;
        })
        .sort((a, b) => orderSeverity.indexOf(a.severity.toUpperCase()) - orderSeverity.indexOf(b.severity.toUpperCase()));
    },
  },
  methods: {
    handleViewDetails(row) {
      const vulId = row.vulId;
      const url = `${this.shareUrl}?vulId=${vulId}`;

      window.open(url, '_blank');
    },
    handleClose() {
      this.$emit('close');
    },
    handleCopy() {
      copyText(this.shareText);
    },
    handleUpdate() {
      this.shareLoading = true;
      const params = {
        validityDays: this.shareTime,
      };

      if (!this.localShareId) {
        params.vulIds = this.vulList.map((item) => item.vulId).filter(Boolean);
        params.taskId = this.taskId;

        getVulShareId(params)
          .then((res) => {
            this.localShareId = res.shareId;
          })
          .finally(() => {
            this.shareLoading = false;
          });

        return;
      }

      updateVulShareTime(this.localShareId, params)
        .then(() => {
          this.$message.success(this.$t('shareDialog.updateSuccess'));
        })
        .finally(() => {
          this.shareLoading = false;
        });
    },
    handleSearch() {
      this.page = 1;
      this.currFilter = { ...this.filter };
    },
    handlePageChange(page) {
      this.page = page;
    },
    getVulnerabilityTagType(category) {
      switch (category) {
        case 'criticalCount':
          return 'info';
        case 'highCount':
          return 'danger';
        case 'mediumCount':
          return 'warning';
        case 'lowCount':
          return 'primary';
        case 'Low':
          return 'success';
        default:
          return 'default';
      }
    },
  },
};
</script>

<style lang="scss">
.shareDialog {
  .el-dialog__header {
    padding: 0;

    .el-dialog__headerbtn {
      top: 12px;
    }

    .el-icon-close {
      font-size: 20px;
      color: #fff !important;
    }
  }
  .el-dialog__body {
    padding: 0;
  }

  .shareDialog--header {
    font-size: 20px;
    font-weight: bold;
    padding: 8px 20px;
    text-align: center;
    background-color: #000;
    color: #fff;
  }

  .shareDialog--body {
    overflow: hidden;
    padding: 24px 20px;
  }

  .shareDialog--body-time {
    margin: 16px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .el-select {
      width: 100px;
    }
  }

  .shareDialog--body-time-item {
    display: flex;
    align-items: center;
  }

  .table-container {
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    padding: 16px 0;
  }

  .table-filter {
    margin-bottom: 16px;
    padding: 0 16px;
    display: flex;
    align-items: center;
  }
}
</style>